import {ReactElement, ReactNode} from "react";
import styled from "styled-components";

import {deleteCampagne} from "@/Api/campagne";
import IconCross from "@/assets/iconCross.svg?react";
import IconTrash from "@/assets/iconTrash.svg?react";
import {AppContext} from "@/context/AppContext";
import {LogContext, LogContextType} from "@/context/LogContex";
import {quicksandBold, quicksandMedium} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {useModal} from "@/hooks/useModal/useModale";
import {AppContext as AppContextType} from "@/ts/appContext";
import {CampagneKpi} from "@/ts/kpi";

import {BlocContent} from "../BlocContent/BlocContent";
import {Button} from "../Button/Button";
import Loader from "../Loader/Loader";

const HistoriqueBloc = styled.div`
  width: 100%;
  container: HistoriqueBloc / inline-size;
`;
const Container = styled.div`
  display: flex;
  @container HistoriqueBloc (width < 1100px) {
    flex-direction: column;
  }
`;

const CampagneHistoriqueContainer = styled.div`
  width: 87%;
  container: CampagneHistoriqueContainer / inline-size;
  @container HistoriqueBloc (width < 1100px) {
    width: 100%;
  }
`;

const CampagneHistoriqueContent = styled.div<{
  $status: "a venir" | "en cours" | "passe";
}>`
  width: calc(100% - 80px);
  padding: 0 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  min-height: 70px;
  & > p {
    max-width: 250px;

    color: ${({$status, theme}) => {
      switch ($status) {
        case "a venir":
          return theme.colors.lightPrimary;
        case "en cours":
          return theme.colors.mediumPrimary;
        case "passe":
          return theme.colors.darkPrimary;
      }
    }};
  }
  @container CampagneHistoriqueContainer ( width < 605px) {
    flex-direction: column;
    width: calc(100% - 30px);
    padding: 0 15px;
    & p {
      text-align: center;
      max-width: 100%;
    }
  }
`;
const TrashButton = styled(Button)`
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: 20px;
  position: absolute;
  top: -5px;
  left: -5px;
  & svg {
    margin: 0 auto;
  }
`;

const NbKpiInfo = styled.div<{
  $status: "a venir" | "en cours" | "passe";
}>`
  width: 90px;
  background-color: ${({$status, theme}) => {
    switch ($status) {
      case "a venir":
        return theme.colors.lightPrimary;
      case "en cours":
        return theme.colors.mediumPrimary;
      case "passe":
        return theme.colors.darkPrimary;
    }
  }};
  & > p {
    text-align: center;
    padding: 7px 0;
    font-size: 1rem;
    ${quicksandBold}
    color:white;
  }
`;

const ChildrenContainer = styled.div`
  margin: 20px 0 20px 15px;
  max-width: max-content;
  flex: 1;
  @container HistoriqueBloc (width < 1100px) {
    width: 100%;
    margin: 0 auto;
  }
`;

const TyrDeleteModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  & h3 {
    margin-bottom: 0.5rem;
    color: ${({theme}) => theme.colors.darkPrimary};

    ${quicksandBold};
  }
  & p {
    margin-bottom: 0.5rem;
    color: ${({theme}) => theme.colors.grey};
    ${quicksandMedium}
  }
`;

const TryDeleteModalButton = styled.div`
  display: flex;
  row-gap: 20px;
  column-gap: 6rem;
  align-items: center;
  justify-content: center;
`;

export function CampagneHistoriqueElement({
  campagne,
  status,
  children,
  refreshCampagnes,
}: {
  campagne: CampagneKpi;
  status: "a venir" | "en cours" | "passe";
  children?: ReactNode;
  refreshCampagnes: () => void;
}): ReactElement {
  const {droits} = useGetContext<LogContextType>(LogContext, "LogContext");
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  const loadingState = useLoadingState();
  const {
    ModalComponent,
    openModal,
    updateContent: modalUpdateContent,
    modalThemeSecondary,
    closeModal,
    modalErrorMessage,
    modalSuccessMessage,
    updateClose,
    lockClose,
    unlockClose,
  } = useModal(() => {}, null);
  const handleDelete = async (): Promise<void> => {
    try {
      modalUpdateContent(<Loader />);
      lockClose();
      await loadingState.executeWithLoading(async () => {
        if (!campagne.campagne_id || campagne.campagne_id === undefined) {
          throw new Error("Campagne ID is not provided.");
        } else {
          await deleteCampagne(API, campagne.campagne_id);
        }
        if (!loadingState.hasError) {
          updateClose(() => {
            refreshCampagnes();
          });
          modalSuccessMessage(
            `La campagne : ${campagne.nom}, a été supprimée avec succès`
          );
        }
      });
      unlockClose();
    } catch (error: any) {
      modalErrorMessage(
        `Erreur lors de la suppression de la campagne : ${error.message}`
      );
      unlockClose();
    }
  };
  const handleTryDelete = async (): Promise<void> => {
    loadingState.reset();
    modalThemeSecondary();
    modalUpdateContent(
      <TyrDeleteModal>
        <h3>{`Vous allez supprimer la campagne : ${campagne.nom}`}</h3>
        <p>Êtes-vous sûr de vouloir supprimer la campagne ?</p>
        <TryDeleteModalButton>
          <Button
            label="Annuler"
            variant="negatif_secondary"
            icon={IconCross}
            onClick={() => closeModal()}
          />
          <Button
            label="Supprimer"
            icon={IconTrash}
            onClick={() => handleDelete()}
          />
        </TryDeleteModalButton>
      </TyrDeleteModal>
    );
    openModal();
  };
  return (
    <HistoriqueBloc>
      <Container>
        <CampagneHistoriqueContainer>
          <BlocContent>
            {droits?.["coact.campagne.supprimer"] && (
              <TrashButton
                isCircle={true}
                icon={IconTrash}
                onClick={() => handleTryDelete()}
              />
            )}
            <CampagneHistoriqueContent $status={status}>
              <p>{campagne.nom}</p>
              <NbKpiInfo $status={status}>
                <p>{`${campagne?.kpis?.length} KPI`}</p>
              </NbKpiInfo>
            </CampagneHistoriqueContent>
          </BlocContent>
        </CampagneHistoriqueContainer>
        <ChildrenContainer>{children}</ChildrenContainer>
      </Container>
      {ModalComponent}
    </HistoriqueBloc>
  );
}
